<template>
  <div v-if="log.data" class="flex space-x-3">
    <div
      class="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
      :class="isGmail ? 'border bg-white' : 'bg-secondary-blue-4'"
    >
      <icon-base
        :icon="isGmail ? IconGoogleColored : IconEmail"
        class="text-white"
        :view-box="isGmail ? '0 0 12 12' : '0 0 16 16'"
      />
      <div
        class="absolute -right-2-5 -bottom-2-5 flex h-6 w-6 items-center justify-center rounded-full border-2 border-disabled bg-turquoise"
      >
        <icon-base
          :icon="IconIncomingArrow"
          height="12"
          width="12"
          class="text-white"
          :class="{ 'rotate-180 transform': log.data.is_outgoing }"
        />
      </div>
    </div>
    <div class="flex grow flex-col divide-y rounded-md bg-white shadow-main">
      <div class="flex justify-between bg-grey-hover px-5 py-3">
        <span
          class="cursor-pointer"
          data-cy="email-deal-link"
          @click="openDeal"
        >
          {{
            log.data.emailable?.business?.business_legal_name ??
            $t("EMAIL_INTEGRATION.NO_DEAL_ASSIGNED")
          }}
        </span>
        <div v-if="isInboxPage" class="relative">
          <assign-messageable-to-deal
            :id="log.data.id"
            :application-id="log.data.emailable?.application_id"
            :name="log.data.emailable?.business?.business_legal_name"
            :assign-text="assignText"
            :type="CommunicationType.email"
          />
        </div>
      </div>
      <div class="px-6 py-4">
        <div class="space-y-3">
          <div class="flex items-center space-x-2">
            <span class="text-xs text-label">{{ $t("COMMON.SUBJECT") }}:</span>
            <span
              class="text-left text-base font-semibold text-headline"
              data-cy="email-subject"
            >
              {{ log.data.subject }}
            </span>
          </div>
          <content-show-hide
            v-if="log.data.content_text"
            :content="log.data.content_text"
          />
        </div>
      </div>
      <div class="px-6 py-2">
        <div class="flex-row-full-justify-between">
          <div class="flex items-center space-x-2">
            <span class="font-medium">{{ $t("ACTIVITY.EMAIL.FROM") }}:</span>
            <span class="text-headline" data-cy="email-from">
              {{ log.data.from }}
            </span>
            <template v-if="log.data.created_at">
              <span>&#8226;</span>
              <date-time :date="log.data.created_at" />
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { CommunicationType } from "@/enums/communicationLogs";
import {
  useCommunicationLogs,
  useEmailLogInfo,
} from "@/hooks/communicationLogs";
import { ROUTE_DEAL_PROGRESS } from "@/router/routes";

import ContentShowHide from "@/components/communicationLogs/communication/ContentShowHide.vue";
import AssignMessageableToDeal from "@/components/communicationLogs/communication/emailThread/AssignMessageableToDeal.vue";
import IconEmail from "@/components/icons/IconEmail.vue";
import IconGoogleColored from "@/components/icons/IconGoogleColored.vue";
import IconIncomingArrow from "@/components/icons/IconIncomingArrow.vue";
import DateTime from "@/components/ui/ConvertibleDateTime.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { EmailLog } from "@/models/communicationLogs";

const props = defineProps<{ log: EmailLog }>();

const router = useRouter();
const { isInboxPage } = useCommunicationLogs();
const { t } = useI18n();
const { isGmail } = useEmailLogInfo(props.log.data);

const assignText = computed(() =>
  props.log.data.emailable?.business?.business_legal_name
    ? t("EMAIL_INTEGRATION.REASSIGN")
    : t("EMAIL_INTEGRATION.ASSIGN_TO_DEAL")
);

const applicationId = computed(() =>
  props.log.data?.emailable?.application_id?.toString()
);

const dealRoute = computed(() =>
  router.resolve({
    name: ROUTE_DEAL_PROGRESS,
    params: { id: applicationId.value },
  })
);

const openDeal = () => {
  if (!applicationId.value) {
    return;
  }

  window.open(dealRoute.value.href, "_blank");
};
</script>
